import { Tooltip } from 'components/ui/tooltip';
import { ReactNode } from 'react';
import { t } from 'utils/getTranslations';

type Props = {
    /** How many entries the cell had no room for. */
    count: number;
    /** The full list, one entry per line — the badge is the only way to reach the rest. */
    title: ReactNode;
};

/**
 * The `+N` badge a cell shows when it can display one of its entries and count the others.
 *
 * An explicit 4px radius, not `rounded`: the app sets `--radius: 10px`, which on something this
 * short reads as a pill. The tooltip stays open while the pointer travels from the badge into it,
 * which matters whenever the lines in there are links.
 */
export function MoreCountBadge({ count, title }: Props): JSX.Element {
    return (
        <Tooltip content={title}>
            <span className="text-caption bg-link/7 text-link rounded-[4px] px-1.5 font-semibold">
                {t('timecards.list.plus-count', { count: String(count) })}
            </span>
        </Tooltip>
    );
}

/**
 * A tooltip body of plain lines. Links get their own treatment per cell, since the app's black
 * hyperlink is not something a portaled tooltip inherits.
 */
export function TooltipLines({ lines }: { lines: string[] }): JSX.Element {
    return (
        <span className="flex flex-col">
            {lines.map((line) => (
                <span key={line}>{line}</span>
            ))}
        </span>
    );
}
